// Investigation for manual enrolment

import axios, { AxiosInstance } from "axios";
import { globalConfig } from "../../Common/configuration/config";

/**
 * Client for Auth0's My Account API (`/me/v1`).
 *
 * WHY THIS DOES NOT EXTEND `axiosClient`
 * --------------------------------------
 * `axiosClient` is hard-wired to `config.ApiUrl` (the identity API) and wraps
 * every response in this platform's `CommandResult`/`QueryResult` envelopes.
 * The My Account API is a different host with a different response shape, so
 * reusing that base class would mean fighting it on both counts.
 *
 * WHY THE BROWSER CALLS THIS DIRECTLY
 * -----------------------------------
 * The My Account API cannot be called with client credentials - Auth0 only
 * accepts a token issued to the logged-in user themselves. That rules out
 * brokering enrollment through a back-end service, and it is why the WebAuthn
 * ceremony has to happen here, in a page the user is signed in to.
 */

/** Auth0 requires the trailing slash on the My Account audience. */
export function myAccountAudience(): string {
    return `https://${globalConfig.get().Domain}/me/`;
}

/** The only scope needed to enrol a new authentication method. */
export const MY_ACCOUNT_ENROLLMENT_SCOPE = "create:me:authentication_methods";

export interface StartPasskeyEnrollmentResponse {
    auth_session: string;
    authn_params_public_key: any;
}

export class myAccountClient {
    private instance: AxiosInstance;
    private baseUrl: string;

    constructor() {
        this.instance = axios.create();
        this.baseUrl = `https://${globalConfig.get().Domain}/me/v1`;
    }

    private authorize(token: string) {
        return {
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json"
            }
        };
    }

    /**
     * Asks Auth0 for a credential creation challenge.
     *
     * The returned `authn_params_public_key` includes `excludeCredentials`,
     * listing every passkey already on the account. That list is what lets the
     * authenticator tell us this device is already enrolled - see
     * `createPasskeyCredential` in `webauthn.ts`.
     */
    async startPasskeyEnrollment(token: string, connection: string): Promise<StartPasskeyEnrollmentResponse> {
        const response = await this.instance.post(
            `${this.baseUrl}/authentication-methods`,
            { type: "passkey", connection },
            this.authorize(token)
        );

        return response.data;
    }

    /**
     * Hands the signed attestation back to Auth0 to complete enrollment.
     *
     * The `passkey|new` path segment is literal - the pipe is percent-encoded
     * so it survives the URL.
     */
    async verifyPasskeyEnrollment(token: string, authSession: string, authnResponse: any): Promise<any> {
        const response = await this.instance.post(
            `${this.baseUrl}/authentication-methods/${encodeURIComponent("passkey|new")}/verify`,
            { auth_session: authSession, authn_response: authnResponse },
            this.authorize(token)
        );

        return response.data;
    }
}
